import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from 'react-router-dom';
import {
  Calendar,
  Clock,
  Plus,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeft,
  Send,
  FileText,
  User,
  Download,
  Mail,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Trash2,
  Info,
  Loader2,
  Upload,
  ChevronDown,
} from "lucide-react";
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import Button from "../../../components/common/Button/Button";
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../context/AuthContext';
import { formatDate } from "../../../utils/stringUtils";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import WarningPopup from "../../../components/WarningPopup";
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import { INVALID_CHARS_REGEX } from "../../../utils/formValidation";
import LeaveRequestForm from "../../../components/hrms/LeaveRequestForm";

const extractCloudinaryDetails = (url) => {
  if (!url || !url.includes('cloudinary.com')) return { publicId: '', resourceType: 'raw' };
  
  try {
    const parts = url.split('/');
    const uploadIndex = parts.indexOf('upload');
    if (uploadIndex === -1) return { publicId: '', resourceType: 'raw' };
    
    const resourceType = parts[uploadIndex - 1] || 'raw';
    
    let publicIdWithExt = '';
    const afterUpload = parts.slice(uploadIndex + 1);
    
    if (afterUpload[0] && afterUpload[0].startsWith('v') && /^\d+$/.test(afterUpload[0].slice(1))) {
      publicIdWithExt = afterUpload.slice(1).join('/');
    } else {
      publicIdWithExt = afterUpload.join('/');
    }
    
    const lastDotIndex = publicIdWithExt.lastIndexOf('.');
    const publicId = lastDotIndex !== -1 ? publicIdWithExt.substring(0, lastDotIndex) : publicIdWithExt;
    
    return { publicId, resourceType };
  } catch (error) {
    return { publicId: '', resourceType: 'raw' };
  }
};

const LeaveRequests = ({ onBack, userRole = "hr_manager", initialMode = "list", id, onClose }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { hasPermission, canCreate, canUpdate } = usePermissions();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [currentUserEmployeeId, setCurrentUserEmployeeId] = useState(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || "all");
  const [filterType, setFilterType] = useState(searchParams.get('leaveType') || "all");
  const [filterStartDate, setFilterStartDate] = useState(searchParams.get('startDate') || "");
  const [filterEndDate, setFilterEndDate] = useState(searchParams.get('endDate') || "");
  const [searchMetadata, setSearchMetadata] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(initialMode === "create");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(initialMode === "view");
  const [editingRequest, setEditingRequest] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeeLeaveInfoMap, setEmployeeLeaveInfoMap] = useState({});
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // State for searchable employee dropdown
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");

  // Store selected employee data to persist even after search is cleared
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);

  // Leave policies state
  const [leavePolicies, setLeavePolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);

  // Get query client for invalidating queries
  const queryClient = useQueryClient();

  // Pagination state - matching regularization pattern
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('limit')) || 10);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    leavePolicyId: "",
    startDate: "",
    endDate: "",
    reason: "",
    status: "pending",
    isActive: true,
    employeeId: "",
    employeeName: "",
    employeeEmail: "",
    requestedDays: 0,
    isHalfDay: false,
    halfDayType: "first_half",
    documentUrl: "",
    documentPublicId: "",
    documentResourceType: "raw"
  });

  // Form validation state
  const [formErrors, setFormErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Confirmation modal states
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [pendingActionRequest, setPendingActionRequest] = useState(null);
  const [rejectionComment, setRejectionComment] = useState("");
  const [approvalComment, setApprovalComment] = useState("");

  // Sandwich rule confirmation states
  const [showSandwichConfirm, setShowSandwichConfirm] = useState(false);
  const [sandwichValidationResult, setSandwichValidationResult] = useState(null);
  const [isValidatingSandwich, setIsValidatingSandwich] = useState(false);

  // Export and row selection states
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);

  // Sorting state
  const [sortField, setSortField] = useState(searchParams.get('sortBy') || 'createdAt');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc'); // 'asc' or 'desc'

  const loadLeaveRequests = async (leaveInfoMapOverride = null) => {
    const leaveInfoLookup = leaveInfoMapOverride || employeeLeaveInfoMap;
    setLoading(true);
    setIsSearching(true);
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }

      if (filterStatus !== 'all') {
        queryParams.append('status', filterStatus);
      }

      if (filterType !== 'all') {
        queryParams.append('leaveType', filterType);
      }

      if (filterStartDate) {
        queryParams.append('startDate', filterStartDate);
      }

      if (filterEndDate) {
        queryParams.append('endDate', filterEndDate);
      }

      // Add pagination
      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', itemsPerPage.toString());
      
      // Add sorting
      if (sortField) {
        queryParams.append('sortBy', sortField);
        queryParams.append('sortOrder', sortOrder);
      }

      const url = `${process.env.REACT_APP_API_BASE_URL}/leave-management?${queryParams.toString()}`;
      const response = await authFetch(url, {
        credentials: 'include',
        headers: {
                  }
      });
      const data = await response.json();

      const isSuccess =
        data?.success === true ||
        data?.status === "success" ||
        data?.responseCode === 200;

      if (isSuccess) {
        setSearchMetadata(data.data.searchMetadata || null);

        // Transform the API response to match the expected format
        const transformedRequests = (data.data.requests || []).map(request => {
          const populatedEmployee = request.employeeId && typeof request.employeeId === 'object'
            ? request.employeeId
            : null;
          const employeeKey =
            (populatedEmployee?._id && populatedEmployee._id.toString()) ||
            (typeof request.employeeId === 'string' ? request.employeeId : null) ||
            request.employeeUniqueId ||
            request.employee;

          // Prioritize historical balance from the request itself
          const existingBalance = request.leaveBalance || {};
          
          // Fallback to map only if historical balance is missing
          const leaveInfoFromMap =
            employeeKey && leaveInfoLookup
              ? leaveInfoLookup[employeeKey.toString()]
              : undefined;

          const leaveInfo = leaveInfoFromMap || populatedEmployee?.leaveInfo || {};
          const leaveTypes = leaveInfo?.leaveTypes || [];
          const leaveTypeInfo = leaveTypes.find(lt => lt.type === request.leaveType);

          let policyEntitlement = 0;
          if (request.leavePolicyId && leavePolicies.length > 0) {
            const policy = leavePolicies.find(
              (p) => p._id === request.leavePolicyId || p.id === request.leavePolicyId
            );
            policyEntitlement = policy?.entitlement || 0;
          }

          const derivedTotal =
            existingBalance.total ??
            leaveTypeInfo?.total ??
            (leaveTypeInfo?.used !== undefined && leaveTypeInfo?.remaining !== undefined
              ? (leaveTypeInfo.used || 0) + (leaveTypeInfo.remaining || 0)
              : policyEntitlement ?? 0);

          const derivedUsed = existingBalance.used ?? leaveTypeInfo?.used ?? 0;

          const derivedRemaining =
            existingBalance.remaining ??
            leaveTypeInfo?.remaining ??
            (policyEntitlement
              ? Math.max(0, policyEntitlement - derivedUsed)
              : Math.max(0, derivedTotal - derivedUsed));

          const normalizedBalance = {
            total: derivedTotal,
            used: derivedUsed,
            remaining: derivedRemaining
          };

          return {
            id: request.id || request._id,
            employeeId: request.employeeId?._id || request.employeeId,
            employeeName: request.employeeName ||
              (request.employeeId?.personalInfo
                ? `${request.employeeId.personalInfo.firstName || ''} ${request.employeeId.personalInfo.lastName || ''}`.trim()
                : 'Unknown Employee'),
            employeeEmail: request.employeeEmail ||
              request.employeeId?.contactInfo?.personalEmail ||
              "N/A",
            department: request.department ||
              request.employeeId?.employmentInfo?.department ||
              "Unknown",
            leaveType: request.leaveType,
            leavePolicyId: request.leavePolicyId,
            startDate: request.startDate,
            endDate: request.endDate,
            requestedDays: request.requestedDays || request.totalDays,
            reason: request.reason,
            status: request.status || "pending",
            appliedDate: request.createdAt || request.appliedDate,
            appliedDateTime: request.appliedDateTime,
            raisedBy: request.raisedBy || null,
            approvedBy: request.approvedBy,
            approvedDate: request.approvedDate,
            approvedDateTime: request.approvedDateTime,
            comments: request.comments,
            isActive: request.isActive !== undefined ? request.isActive : true,
            leaveBalance: normalizedBalance,
            documentUrl: request.documentUrl || null,
            isHalfDay: request.isHalfDay || false,
            halfDayType: request.halfDayType || null,
            sandwichDays: request.sandwichDays || 0,
            effectiveDays: request.effectiveDays || 0
          };
        });

        setLeaveRequests(transformedRequests);

        // Update pagination state from API response
        if (data.data.pagination) {
          setPagination({
            currentPage: data.data.pagination.currentPage || currentPage,
            totalPages: data.data.pagination.totalPages || 1,
            totalItems: data.data.pagination.totalRequests || data.data.pagination.totalItems || 0,
            itemsPerPage: data.data.pagination.limit || itemsPerPage,
            hasNextPage: data.data.pagination.hasNextPage ?? false,
            hasPrevPage: data.data.pagination.hasPrevPage ?? false,
            limit: data.data.pagination.limit || itemsPerPage
          });
        }
      } else {
        const errorMessage = data?.message || data?.error || "Unknown error";
        setLeaveRequests([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: itemsPerPage,
          hasNextPage: false,
          hasPrevPage: false,
          limit: itemsPerPage
        });
        setSearchMetadata(null);
      }
    } catch (error) {
      setLeaveRequests([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: itemsPerPage,
        hasNextPage: false,
        hasPrevPage: false,
        limit: itemsPerPage
      });
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/leaverequest`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Check for success using responseCode or success property
      if (result.success === true || result.responseCode === 200) {
        const employeesData = result.data || [];
        setEmployees(employeesData);

        const infoMap = {};
        employeesData.forEach((employee) => {
          const key =
            employee.id ||
            employee._id ||
            employee.employeeId ||
            employee.employeeUniqueId;
          if (key) {
            infoMap[key.toString()] = employee.leaveInfo || {};
          }
        });

        setEmployeeLeaveInfoMap(infoMap);
        await loadLeaveRequests(infoMap);
      } else {
        toast.error(
          "Failed to load employees: " + (result.responseMessage || result.message || "Unknown error")
        );
      }
    } catch (error) {
      toast.error(
        "Failed to load employees. Please check your connection and try again."
      );
      setEmployees([]);
    }
  };


  // Debounce employee search for dropdown
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 300); // 300ms debounce delay for employee search

    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // Handle deep-linking to a specific request
  useEffect(() => {
    if (id && initialMode === "view") {
      const fetchRequestById = async () => {
        setLoading(true);
        try {
          const url = `${process.env.REACT_APP_API_BASE_URL}/leave-management/${id}`;
          const response = await authFetch(url, {
        credentials: 'include',
            headers: {
                          }
          });
          const result = await response.json();
          if ((result.success || result.status === "success") && result.data) {
            setSelectedRequest(result.data);
            setShowDetails(true);
          } else {
          }
        } catch (error) {
        } finally {
          setLoading(false);
        }
      };
      fetchRequestById();
    }
  }, [id, initialMode]);

  // React Query: Search Employees (fetch when dropdown is open, load initial if search empty)
  const { data: searchedEmployeesData, isLoading: isLoadingEmployeeSearch } = useQuery({
    queryKey: ['leave-requests-employees-search', debouncedEmployeeSearch],
    queryFn: async () => {
      const trimmedSearch = debouncedEmployeeSearch?.trim() || "";
      const url = `${process.env.REACT_APP_API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(trimmedSearch)}&limit=50`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          return data.data.employees || [];
        }
      }
      return [];
    },
    enabled: employeeSearchOpen, // Call when search dropdown is open
    staleTime: 0,
  });

  // React Query: Delete Leave Request Mutation
  const deleteRequestMutation = useMutation({
    mutationFn: async (requestId) => {
      const url = `${process.env.REACT_APP_API_BASE_URL}/leave-management/${requestId}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
                    'Content-Type': 'application/json'
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.responseMessage || errorData.message || 'Failed to delete leave request');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leave-requests"] });
      // Also invalidate stats if they exist
    },
  });

  // Get employees to display in dropdown (only searched results, no initial load)
  const dropdownEmployees = searchedEmployeesData || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (employeeSearchOpen && !event.target.closest('.employee-dropdown-container')) {
        setEmployeeSearchOpen(false);
        setEmployeeSearchTerm("");
      }
    };

    if (employeeSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [employeeSearchOpen]);

  // Close dropdown when form closes (but preserve selectedEmployeeData)
  useEffect(() => {
    if (!showRequestForm) {
      setEmployeeSearchOpen(false);
      setEmployeeSearchTerm("");
      // Don't clear selectedEmployeeData here - it should persist until form is reset
    }
  }, [showRequestForm]);

  // Load leave policies
  const loadLeavePolicies = async () => {
    try {
      setLoadingPolicies(true);
      const url = `${process.env.REACT_APP_API_BASE_URL}/leave-policies/leave?isActive=true`;

      const response = await authFetch(url, {
        credentials: 'include',
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === "success" || result.responseCode === 200) {
        const policies = result.data || [];
        setLeavePolicies(policies);
      } else {
        setLeavePolicies([]);
      }
    } catch (error) {
      setLeavePolicies([]);
    } finally {
      setLoadingPolicies(false);
    }
  };

  useEffect(() => {
    loadLeavePolicies();
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      // Trigger load when debounced search changes
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterType, itemsPerPage, filterStartDate, filterEndDate]);

  // Load data when pagination or filters change (with debounce for search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadLeaveRequests();
    }, searchTerm ? 300 : 0); // Debounce only for search, immediate for other filters

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterStatus, filterType, currentPage, itemsPerPage, employeeLeaveInfoMap, filterStartDate, filterEndDate]);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'leaveType', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (searchTerm) params.set('search', searchTerm);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterType && filterType !== 'all') params.set('leaveType', filterType);
      if (filterStartDate) params.set('startDate', filterStartDate);
      if (filterEndDate) params.set('endDate', filterEndDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortField && sortField !== 'createdAt') params.set('sortBy', sortField);
      if (sortOrder && sortOrder !== 'desc') params.set('sortOrder', sortOrder);

      return params;
    }, { replace: true });
  }, [searchTerm, filterStatus, filterType, filterStartDate, filterEndDate, currentPage, itemsPerPage, sortField, sortOrder, setSearchParams]);

  useEffect(() => {
    loadEmployees();
  }, []);

  // Fetch current user's employee ID
  useEffect(() => {
    const fetchCurrentUserEmployeeId = async () => {
      if (!user?._id) return;

      try {
        // Use ESS profile endpoint which gets employee by logged-in user
        const response = await authFetch(
          `${process.env.REACT_APP_API_BASE_URL}/ess/profile`, {
        credentials: 'include',
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              }
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.responseCode === 200 && result.data) {
            const employee = result.data;
            if (employee?._id) {
              setCurrentUserEmployeeId(employee._id);
            }
          }
        } else {
          // Fallback: try using hr/employees endpoint with user ID
          const fallbackResponse = await authFetch(
            `${process.env.REACT_APP_API_BASE_URL}/hr/employees/${user._id}`, {
        credentials: 'include',
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                }
            }
          );

          if (fallbackResponse.ok) {
            const fallbackResult = await fallbackResponse.json();
            if (fallbackResult.responseCode === 200 && fallbackResult.data) {
              const employee = fallbackResult.data;
              if (employee?._id) {
                setCurrentUserEmployeeId(employee._id);
              }
            }
          }
        }
      } catch (error) {
      }
    };

    fetchCurrentUserEmployeeId();
  }, [user]);

  // Calculate days helper function
  const calculateDays = useCallback((startDate, endDate, isHalfDay = false) => {
    if (!startDate || !endDate) return 0;
    if (isHalfDay) return 0.5;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  }, []);

  // Auto-calculate total days when start or end date changes
  useEffect(() => {
    if (formData.startDate && (formData.isHalfDay || formData.endDate)) {
      const actualEndDate = formData.isHalfDay ? formData.startDate : formData.endDate;
      const days = calculateDays(formData.startDate, actualEndDate, formData.isHalfDay);
      setFormData((prev) => ({
        ...prev,
        requestedDays: days,
        // If half day, ensure end date matches start date
        ...(formData.isHalfDay ? { endDate: formData.startDate } : {})
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        requestedDays: 0,
      }));
    }
  }, [formData.startDate, formData.endDate, formData.isHalfDay, calculateDays]);

  // Reset row selection when filters, search, or pagination changes
  useEffect(() => {
    setSelectedRows(new Set());
  }, [searchTerm, filterStatus, filterType, currentPage, pageSize, filterStartDate, filterEndDate]);

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setPageSize(limit);
    setCurrentPage(1);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "pending":
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };


  // Validation functions
  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,15}$/;
    return phoneRegex.test(phone);
  };

  const validateTextLength = (text, minLength, maxLength) => {
    if (!text || typeof text !== 'string') {
      return `Must be at least ${minLength} characters`;
    }
    const trimmedText = text.trim();
    if (trimmedText.length < minLength) {
      return `Must be at least ${minLength} characters`;
    }
    if (trimmedText.length > maxLength) {
      return `Must be no more than ${maxLength} characters`;
    }
    return null;
  };

  const validateLeaveDuration = (startDate, endDate, leavePolicyId, isHalfDay = false) => {
    if (!startDate || (!isHalfDay && !endDate)) return null;

    const actualEndDate = isHalfDay ? startDate : endDate;
    const requestedDays = calculateDays(startDate, actualEndDate, isHalfDay);
    const selectedPolicy = leavePolicies.find(p =>
      p._id === leavePolicyId || p.id === leavePolicyId
    );

    if (selectedPolicy && requestedDays > selectedPolicy.entitlement) {
      return `Maximum ${selectedPolicy.entitlement} days allowed for ${selectedPolicy.name}`;
    }

    if (requestedDays > 365) {
      return "Leave duration cannot exceed 365 days";
    }

    if (requestedDays < 0.5) {
      return "End date must be on or after start date";
    }

    return null;
  };

  const validateLeaveBalance = async (leaveInfo = {}, leaveType, requestedDays, policyEntitlement = 0) => {
    try {
      if (!leaveType || !requestedDays) {
        return null;
      }

      const leaveTypes = leaveInfo.leaveTypes || [];
      const leaveTypeInfo = leaveTypes.find(lt => lt.type === leaveType);

      let remainingDays = 0;

      if (leaveTypeInfo) {
        const total = leaveTypeInfo.total ?? policyEntitlement ?? 0;
        const used = leaveTypeInfo.used ?? 0;
        remainingDays =
          leaveTypeInfo.remaining !== undefined
            ? leaveTypeInfo.remaining
            : Math.max(0, total - used);
      } else {
        // When employee does not yet have balance for this leave type,
        // default to policy entitlement (fallback to overall remaining leaves).
        if (policyEntitlement) {
          remainingDays = policyEntitlement;
        } else if (leaveInfo.remainingLeaves !== undefined) {
          remainingDays = leaveInfo.remainingLeaves;
        } else {
          remainingDays = 0;
        }
      }

      if (requestedDays > remainingDays) {
        const typeSuffix = leaveType ? ` for ${leaveType}` : "";
        return `Insufficient leave balance. You have ${remainingDays} days remaining${typeSuffix}, but requesting ${requestedDays} days.`;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  // Field-level validation function
  const validateField = async (name, value) => {
    let error = "";

    switch (name) {
      case "employeeId":
        if (!value) {
          error = "Please select an employee";
        }
        break;

      case "leavePolicyId":
        if (!value) {
          error = "Please select a leave policy";
        } else {
          // Check if policy exists
          const selectedPolicy = leavePolicies.find(p =>
            p._id === value || p.id === value
          );
          if (!selectedPolicy) {
            error = "Selected leave policy not found. Please refresh and try again.";
          }
        }
        break;

      case "startDate":
        if (!value) {
          error = "Start date is required";
        } else {
          /* 
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const startDate = new Date(value);
          startDate.setHours(0, 0, 0, 0);
          if (startDate < today) {
            error = "Start date cannot be in the past";
          }
          */
        }
        break;

      case "endDate":
        if (!value) {
          error = "End date is required";
        } else if (formData.startDate && value) {
          const startDate = new Date(formData.startDate);
          const endDate = new Date(value);
          if (endDate < startDate) {
            error = "End date must be after start date";
          }
        }
        break;

      case "reason":
        if (!value || !value.trim()) {
          error = "Reason is required";
        } else if (value.length > 200) {
          error = "Reason / Note must be ≤ 200 characters";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const validateForm = async () => {
    const errors = {};
    const fieldsToValidate = ["employeeId", "leavePolicyId", "startDate", "endDate", "reason"];

    // Validate all fields
    for (const field of fieldsToValidate) {
      const error = await validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
      }
    }

    // Leave duration validation - Handled by backend
    /* 
    const durationError = validateLeaveDuration(formData.startDate, formData.endDate, formData.leavePolicyId, formData.isHalfDay);
    if (durationError) {
      errors.duration = durationError;
    }
    */

    // Leave balance validation - Handled by backend
    /*
    if (formData.employeeId && formData.leavePolicyId && formData.startDate && (formData.isHalfDay || formData.endDate)) {
      const requestedDays = formData.requestedDays || calculateDays(formData.startDate, formData.isHalfDay ? formData.startDate : formData.endDate, formData.isHalfDay);
      // Get leave type from selected policy - check both _id and id fields
      const selectedPolicy = leavePolicies.find(p =>
        p._id === formData.leavePolicyId || p.id === formData.leavePolicyId
      );
      const leaveType = selectedPolicy?.type || '';
      if (!selectedPolicy) {
        errors.leavePolicyId = "Selected leave policy not found. Please refresh and try again.";
        return errors;
      }
      const balanceError = await validateLeaveBalance(
        selectedEmployeeData?.leaveInfo,
        leaveType,
        requestedDays,
        totalDays: requestedDays, // fallback
        selectedPolicy.entitlement
      );
      if (balanceError) {
        errors.balance = balanceError;
      }
    }
    */

    return errors;
  };

  // Handle key press for text inputs - allow approved symbols
  const handleTextKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };

  // Handle key down for number inputs (prevent negative signs and decimals)
  const handleNumberKeyDown = (e) => {
    // Prevent negative sign, decimal point, and 'e' or 'E' (scientific notation)
    if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    let processedValue = value || '';

    // Text fields: only allow alphanumeric, spaces, and commas
    if (name === 'reason') {
      processedValue = (value || '').replace(INVALID_CHARS_REGEX, '');
      // Enforce maxLength
      if (processedValue && processedValue.length > 200) processedValue = processedValue.slice(0, 200);
    } else if (name === 'startDate' || name === 'endDate') {
      // For dates, update requestedDays automatically via useEffect
      processedValue = value;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue || value || '',
    }));

    // Field-level validation as user interacts
    const error = await validateField(name, processedValue || value);
    setFormErrors(prev => ({
      ...prev,
      [name]: error
    }));

    // If end date changed and start date exists, also validate start date
    if (name === 'endDate' && formData.startDate) {
      const startDateError = await validateField('startDate', formData.startDate);
      setFormErrors(prev => ({
        ...prev,
        startDate: startDateError
      }));
    }
  };

  const handleBlur = async (e) => {
    const { name, value } = e.target;
    const error = await validateField(name, value);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const processSubmit = async () => {
    try {
      const requestedDays = formData.requestedDays || calculateDays(formData.startDate, formData.endDate);

      // Get selected policy details
      const selectedPolicy = leavePolicies.find(p =>
        p._id === formData.leavePolicyId || p.id === formData.leavePolicyId
      );

      if (!selectedPolicy) {
        toast.error(`Leave policy not found.`);
        return;
      }
      const selectedEmployeeId = selectedEmployeeData?._id || formData.employeeId;
      const leaveType = selectedPolicy.type;

      // Prepare payload
      const payload = {
        employeeId: selectedEmployeeId,
        employeeName: formData.employeeName,
        employeeEmail: formData.employeeEmail,
        leavePolicyId: formData.leavePolicyId,
        leaveType: leaveType,
        startDate: formData.startDate,
        endDate: formData.isHalfDay ? formData.startDate : formData.endDate,
        requestedDays: requestedDays,
        totalDays: requestedDays,
        isHalfDay: formData.isHalfDay,
        halfDayType: formData.isHalfDay ? formData.halfDayType : null,
        reason: formData.reason || "",
        status: formData.status || "pending",
        isActive: formData.isActive !== undefined ? formData.isActive : true,
        raisedBy: user?._id || null,
        documentUrl: formData.documentUrl || "",
      };

      const url = editingRequest
        ? `${process.env.REACT_APP_API_BASE_URL}/leave-management/${editingRequest.id}`
        : `${process.env.REACT_APP_API_BASE_URL}/leave-management`;

      const method = editingRequest ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        credentials: 'include',
        method: method,
        headers: {
          'Content-Type': 'application/json',
          },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorResult = await response.json().catch(() => ({}));
        throw new Error(errorResult.responseMessage || errorResult.message || 'Failed to process leave request');
      }

      toast.success(editingRequest ? "Leave request updated successfully!" : "Leave request created successfully!");
      
      await loadLeaveRequests();
      if (onClose) onClose();

      // Reset form
      setFormData({
        leavePolicyId: "",
        startDate: "",
        endDate: "",
        reason: "",
        status: "pending",
        isActive: true,
        employeeId: "",
        employeeName: "",
        employeeEmail: "",
        requestedDays: 0,
        isHalfDay: false,
        halfDayType: "first_half",
        documentUrl: "",
        documentPublicId: "",
        documentResourceType: "raw"
      });
      setFormErrors({});
      setEditingRequest(null);
      setSelectedEmployee(null);
      setSelectedEmployeeData(null);
      setEmployeeSearchOpen(false);
      setEmployeeSearchTerm("");
      setShowRequestForm(false);

    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit.');
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      setIsUploading(true);
      const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/upload/cloudinary`, {
        credentials: 'include',
        method: 'POST',
        
        body: formDataObj
      });

      const result = await response.json();
      if (result.success && result.data && result.data.url) {
        setFormData(prev => ({
          ...prev,
          documentUrl: result.data.url,
          documentPublicId: result.data.public_id || "",
          documentResourceType: result.data.resource_type || "raw"
        }));
        toast.success('Document uploaded successfully');
      } else {
        toast.error(result.responseMessage || result.message || 'Upload failed');
      }
    } catch (error) {
      toast.error('Error uploading file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (!formData.documentPublicId) {
      setFormData(prev => ({
        ...prev,
        documentUrl: "",
        documentPublicId: "",
        documentResourceType: "raw"
      }));
      return;
    }

    try {
      setIsUploading(true);
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/upload/cloudinary?public_id=${formData.documentPublicId}&resource_type=${formData.documentResourceType}`, {
        credentials: 'include',
          method: 'DELETE',
          headers: {
            }
        }
      );

      if (response.ok) {
        setFormData(prev => ({
          ...prev,
          documentUrl: "",
          documentPublicId: "",
          documentResourceType: "raw"
        }));
        toast.success('Document deleted successfully');
      } else {
        throw new Error('Failed to delete file');
      }
    } catch (error) {
      // Fallback: Clear anyway on error
      setFormData(prev => ({
        ...prev,
        documentUrl: "",
        documentPublicId: "",
        documentResourceType: "raw"
      }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    if (e) e.preventDefault();

    // Check if policies are still loading
    if (loadingPolicies) {
      toast.error("Please wait for leave policies to load before submitting.");
      return;
    }

    // Check if policies are available
    if (leavePolicies.length === 0) {
      toast.error("No leave policies available. Please refresh the page and try again.");
      return;
    }

    // Validate form before submission
    setIsValidating(true);
    const errors = await validateForm();

    if (errors && Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsValidating(false);
      return;
    }

    setFormErrors({});
    setIsValidating(false);

    // Call Sandwich Validation API
    try {
      setIsValidatingSandwich(true);
      const selectedPolicy = leavePolicies.find(p =>
        p._id === formData.leavePolicyId || p.id === formData.leavePolicyId
      );

      const validateResponse = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/leave-management/validate`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          },
        body: JSON.stringify({
          leaveType: selectedPolicy?.type,
          startDate: formData.startDate,
          endDate: formData.isHalfDay ? formData.startDate : formData.endDate,
          employeeId: selectedEmployeeData?._id || formData.employeeId,
          leavePolicyId: formData.leavePolicyId,
          isHalfDay: formData.isHalfDay,
          reason: formData.reason
        })
      });

      const validateResult = await validateResponse.json();

      if (validateResult.success) {
        if (!validateResult.data.isValid) {
          toast.error(validateResult.data.error || "Validation failed");
          return;
        }

        if (validateResult.data.sandwichDays > 0) {
          setSandwichValidationResult(validateResult.data);
          setShowSandwichConfirm(true);
        } else {
          await processSubmit();
        }
      } else {
        throw new Error(validateResult.message || "Validation failed");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsValidatingSandwich(false);
    }
  };

  const handleViewDetails = async (request) => {
    try {
      // Fetch employee data to get leave balance
      const employeeResponse = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/hr/employees/${request.employeeId}`, {
        credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
                      }
        }
      );

      if (employeeResponse.ok) {
        const employeeData = await employeeResponse.json();
        const leaveInfo = employeeData.data?.leaveInfo || {};
        const leaveTypes = leaveInfo.leaveTypes || [];

        // Find the matching leave type balance
        const matchingLeaveType = leaveTypes.find(lt => lt.type === request.leaveType);

        // Format date and time helper
        const formatDateTime = (date) => {
          if (!date) return null;
          let dateObj;
          if (date instanceof Date) {
            dateObj = date;
          } else if (typeof date === 'string') {
            dateObj = new Date(date);
          } else {
            dateObj = new Date(date);
          }

          if (isNaN(dateObj.getTime())) return null;

          // Format as "YYYY-MM-DD | HH:MM:SS"
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          const hours = String(dateObj.getHours()).padStart(2, '0');
          const minutes = String(dateObj.getMinutes()).padStart(2, '0');
          const seconds = String(dateObj.getSeconds()).padStart(2, '0');

          return `${year}-${month}-${day} | ${hours}:${minutes}:${seconds}`;
        };

        // Find policy to get entitlement if leave type not found
        let policyEntitlement = 0;
        if (request.leavePolicyId && leavePolicies.length > 0) {
          const policy = leavePolicies.find(p =>
            p._id === request.leavePolicyId || p.id === request.leavePolicyId
          );
          if (policy) {
            policyEntitlement = policy.entitlement || 0;
          }
        }

        // Always show balance - use policy entitlement if leave type not found
        const total = matchingLeaveType?.total || policyEntitlement || 0;
        const used = matchingLeaveType?.used || 0;
        const remaining = matchingLeaveType?.remaining !== undefined
          ? matchingLeaveType.remaining
          : (total > 0 ? total - used : 0);

        // Add balance info to the request
        const requestWithBalance = {
          ...request,
          comments: request.comments || null, // explicitly preserve — do NOT let spread silently drop it
          appliedDateTime: formatDateTime(request.appliedDate || request.createdAt),
          approvedDateTime: formatDateTime(request.approvedDate || request.approvedAt),
          leaveBalance: {
            total: total,
            used: used,
            remaining: remaining
          }
        };

        setSelectedRequest(requestWithBalance);
        setApprovalComment(""); // reset inline remark field whenever a new request is opened
      } else {
        // If fetch fails, just show the request without balance
        setSelectedRequest(request);
      }
    } catch (error) {
      // If error, just show the request without balance
      setSelectedRequest(request);
    }
    setShowDetails(true);
  };

  const handleApproveRequest = async (requestId, comments = "") => {
    try {
      // Use backend API endpoint which handles balance update
      const approveResponse = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/leave-management/${requestId}/approve`, {
        credentials: 'include',
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            },
          body: JSON.stringify({ comments: comments || 'Leave approved' })
        }
      );

      if (!approveResponse.ok) {
        const errorResult = await approveResponse.json();
        throw new Error(errorResult.message || `Failed to approve leave request: ${approveResponse.status}`);
      }

      // Reload leave requests to reflect the updated status
      await loadLeaveRequests();
      toast.success("Leave request approved successfully!");

      // Close details view if open
      if (showDetails) {
        setShowDetails(false);
      }

      // Close confirmation modal
      setShowApproveConfirm(false);
      setPendingActionRequest(null);
      setApprovalComment("");
    } catch (error) {
      toast.error(`Failed to approve leave request: ${error.message}`);
    }
  };

  const handleRejectRequest = async (requestId, comments = "") => {
    try {
      // Use backend API endpoint
      const rejectResponse = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/leave-management/${requestId}/reject`, {
        credentials: 'include',
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            },
          body: JSON.stringify({ comments: comments || 'Leave rejected' })
        }
      );

      if (!rejectResponse.ok) {
        const errorResult = await rejectResponse.json();
        throw new Error(errorResult.message || `Failed to reject leave request: ${rejectResponse.status}`);
      }

      // Reload leave requests to reflect the updated status
      await loadLeaveRequests();
      toast.success("Leave request rejected successfully!");

      // Close details view if open
      if (showDetails) {
        setShowDetails(false);
      }

      // Close confirmation modal
      setShowRejectConfirm(false);
      setPendingActionRequest(null);
      setRejectionComment("");
    } catch (error) {
      toast.error(`Failed to reject leave request: ${error.message}`);
    }
  };

  // Show approval confirmation modal
  const showApprovalConfirmation = (request) => {
    setPendingActionRequest(request);
    setApprovalComment("");
    setShowApproveConfirm(true);
  };

  // Show rejection confirmation modal
  const showRejectionConfirmation = (request) => {
    setPendingActionRequest(request);
    setRejectionComment("");
    setShowRejectConfirm(true);
  };

  // Confirm approval
  const confirmApproval = () => {
    if (pendingActionRequest) {
      handleApproveRequest(pendingActionRequest.id, approvalComment);
    }
  };

  // Confirm rejection
  const confirmRejection = () => {
    if (!rejectionComment.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    if (pendingActionRequest) {
      handleRejectRequest(pendingActionRequest.id, rejectionComment);
    }
  };

  // Row selection handlers
  const handleSelectAll = (e) => {
    const currentPageIds = leaveRequests.map(req => req.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleDeleteRequest = (request) => {
    setRequestToDelete(request);
    setIsBulkDelete(false);
    setShowDeleteModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteModal(true);
  };

  const confirmDeleteRequest = async () => {
    if (!requestToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deleteRequestMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} leave request(s) deleted successfully.`);
          setSelectedRows(new Set());
        }
        if (failCount > 0) {
          toast.error(`${failCount} leave request(s) could not be deleted.`);
        }
      } else {
        await deleteRequestMutation.mutateAsync(requestToDelete.id);
        toast.success('Leave request deleted successfully.');
      }
      setShowDeleteModal(false);
      setRequestToDelete(null);
      setIsBulkDelete(false);
      await loadLeaveRequests(employeeLeaveInfoMap);
    } catch (error) {
      if (!isBulkDelete) {
        toast.error(error.message || 'Failed to delete leave request. Please try again.');
      }
    }
  };

  const baseExportFilters = useMemo(() => {
    const f = {};
    const trimmed = searchTerm?.trim();
    if (trimmed) f.search = trimmed;
    if (filterStatus !== "all") f.status = filterStatus;
    if (filterType !== "all") f.leaveType = filterType;
    if (filterStartDate) f.startDate = filterStartDate;
    if (filterEndDate) f.endDate = filterEndDate;
    return f;
  }, [searchTerm, filterStatus, filterType, filterStartDate, filterEndDate]);

  const currentPageRequestIds = useMemo(
    () =>
      leaveRequests
        .map((req) => req.id || req._id)
        .filter(Boolean),
    [leaveRequests]
  );

  const exportSortBy = useMemo(() => {
    if (sortField === "employee") return "employee";
    if (sortField === "leaveType") return "leaveType";
    if (sortField === "startDate") return "startDate";
    if (sortField === "endDate") return "endDate";
    if (sortField === "requestedDays") return "requestedDays";
    if (sortField === "status") return "status";
    if (sortField === "appliedDate") return "appliedDate";
    return "createdAt";
  }, [sortField]);

  const exportSortOrder = useMemo(() => {
    if (sortField === null) return "desc";
    return sortOrder;
  }, [sortField, sortOrder]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      currentPageIds,
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const payload = {
        module: "leave-requests",
        format,
        scope,
        filters: overrideFilters ?? baseExportFilters,
        page: overridePage ?? currentPage,
        limit: overrideLimit ?? itemsPerPage,
        sortBy: exportSortBy,
        sortOrder: exportSortOrder,
        selectedIds,
      };
      if (Array.isArray(currentPageIds) && currentPageIds.length > 0) {
        payload.currentPageIds = currentPageIds;
      }
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: "POST",
          headers: {
            "Content-Type": "application/json",
                      },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || "Export request failed";
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage) msg = j.responseMessage;
          else if (j?.error && typeof j.error === "string") msg = j.error;
        } catch {
          /* keep msg */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackExt = format === "xlsx" ? "xlsx" : format;
      const fallbackName = `leave-management-export.${fallbackExt}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      exportSortBy,
      exportSortOrder,
    ]
  );

  const handleLeaveSelectedFormatExport = useCallback(
    async (format) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) return;
      const ids = Array.from(selectedRows);
      if (!ids.length) {
        toast.error("No selected requests to export.");
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: "selected",
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} request(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || "Export failed.");
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleLeaveScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        if (scope === "currentPage" && !leaveRequests.length) {
          toast.error("No requests to export on the current page.");
          return;
        }

        const totalFiltered = Number(pagination?.totalItems ?? 0);
        if (scope === "filtered" && totalFiltered === 0) {
          toast.error("No requests match the current filters.");
          return;
        }

        if (scope === "dateRange") {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error("Please select start and end dates.");
            return;
          }
          await requestBackendExport({
            scope: "dateRange",
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === "all") {
          const trimmed = searchTerm?.trim();
          await requestBackendExport({
            scope: "all",
            format,
            overrideFilters: {
              ...(trimmed ? { search: trimmed } : {}),
              ...(filterStatus !== "all" ? { status: filterStatus } : {}),
              ...(filterType !== "all" ? { leaveType: filterType } : {}),
            },
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === "filtered") {
          await requestBackendExport({
            scope: "filtered",
            format,
          });
        } else if (scope === "currentPage") {
          await requestBackendExport({
            scope: "currentPage",
            format,
            currentPageIds: currentPageRequestIds,
          });
        } else {
          return;
        }

        toast.success("Export downloaded.");
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || "Export failed. Please try again.");
      } finally {
        setExportLoading(false);
      }
    },
    [
      leaveRequests.length,
      pagination?.totalItems,
      baseExportFilters,
      searchTerm,
      filterStatus,
      filterType,
      requestBackendExport,
      currentPageRequestIds,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showInlineSelectedFormats]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Get sorted data
  const getSortedRequests = (requests) => {
    if (!sortField || !requests || requests.length === 0) {
      return requests;
    }

    const sorted = [...requests].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'employee':
          aValue = (a.employeeName || 'Unknown Employee').toLowerCase();
          bValue = (b.employeeName || 'Unknown Employee').toLowerCase();
          break;

        case 'leaveType':
          aValue = (a.leaveType || '').toLowerCase();
          bValue = (b.leaveType || '').toLowerCase();
          break;

        case 'startDate':
          aValue = new Date(a.startDate).getTime();
          bValue = new Date(b.startDate).getTime();
          break;

        case 'requestedDays':
          aValue = a.requestedDays || a.totalDays || 0;
          bValue = b.requestedDays || b.totalDays || 0;
          break;

        case 'balance':
          // Sort by remaining balance
          aValue = a.leaveBalance?.remaining || 0;
          bValue = b.leaveBalance?.remaining || 0;
          break;

        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;

        case 'createdAt':
        case 'appliedDate':
          aValue = new Date(a.appliedDate).getTime();
          bValue = new Date(b.appliedDate).getTime();
          break;

        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Compare values
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };


  const handleEditRequest = (request) => {
    // Set form data with existing request data
    setFormData({
      leavePolicyId: request.leavePolicyId?._id || request.leavePolicyId || "",
      startDate: request.startDate ? request.startDate.split('T')[0] : "",
      endDate: request.endDate ? request.endDate.split('T')[0] : "",
      reason: request.reason || "",
      status: request.status || "pending",
      isActive: request.isActive !== undefined ? request.isActive : true,
      employeeId: request.employeeId?._id || request.employeeId || "",
      employeeName: request.employeeName || "",
      employeeEmail: request.employeeEmail || "",
      requestedDays: request.requestedDays || 0,
      isHalfDay: request.isHalfDay || false,
      halfDayType: request.halfDayType || "first_half",
      documentUrl: request.documentUrl || "",
      documentPublicId: request.documentUrl ? extractCloudinaryDetails(request.documentUrl).publicId : "",
      documentResourceType: request.documentUrl ? extractCloudinaryDetails(request.documentUrl).resourceType : "raw"
    });

    // Clear any existing validation errors
    setFormErrors({});

    // Set editing state
    setEditingRequest(request);
    setShowRequestForm(true);
  };


  return (
    <div className="bg-white rounded-lg shadow-sm">
      {showDetails && selectedRequest ? (
        <>
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowDetails(false);
                    if (onClose) onClose();
                  }}
                  className="p-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <h2 className="text-xl font-semibold text-gray-900">
                  Leave Request Details
                </h2>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Employee Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Employee Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Name:</span>
                    <span className="text-sm font-medium capitalize break-words whitespace-normal">
                      {selectedRequest.employeeName}
                    </span>
                  </div>
                <div className="flex items-start space-x-2">
                  <Mail className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-600">Email: </span>
                    <span className="text-sm font-medium break-words whitespace-normal" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      {selectedRequest.employeeEmail || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">
                    Applied on:
                  </span>
                  <span className="text-sm font-medium">
                    {selectedRequest.appliedDate || (selectedRequest.createdAt ? formatDate(selectedRequest.createdAt, { includeTime: false }) : 'N/A')}
                  </span>
                </div>
                {selectedRequest.status !== "pending" && selectedRequest.approvedDate && (
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      Approved on:
                    </span>
                    <span className="text-sm font-medium">
                      {selectedRequest.approvedDate || (selectedRequest.approvedAt ? formatDate(selectedRequest.approvedAt, { includeTime: false }) : 'N/A')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Leave Information */}
            <div className="bg-brand/5 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Leave Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-gray-600">Type:</span>
                  <span className="text-sm font-medium truncate capitalize">
                    {selectedRequest.leaveType}
                    {selectedRequest.isHalfDay && (
                      <span className="ml-1 text-xs text-brand font-semibold">
                        (Half Day - {selectedRequest.halfDayType === 'first_half' ? '1st Half' : '2nd Half'})
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-gray-600">Duration:</span>
                  <span className="text-sm font-medium">
                    {selectedRequest.startDate ? formatDate(selectedRequest.startDate, { includeTime: false }) : 'N/A'}
                    {!selectedRequest.isHalfDay && (
                      <> to {selectedRequest.endDate ? formatDate(selectedRequest.endDate, { includeTime: false }) : 'N/A'}</>
                    )}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-gray-600">Requested Days:</span>
                  <span className="text-sm font-medium text-brand">
                    {selectedRequest.requestedDays || selectedRequest.totalDays} {(selectedRequest.requestedDays || selectedRequest.totalDays) === 1 ? 'day' : 'days'}
                  </span>
                </div>
                {selectedRequest.sandwichDays > 0 && (
                  <>
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                      <span className="text-sm text-gray-600">Sandwich Deduction:</span>
                      <span className="text-sm font-medium text-amber-600">
                        +{selectedRequest.sandwichDays} Days
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Hash className="h-4 w-4 text-brand" />
                      <span className="text-sm text-gray-600">Effective Deduction:</span>
                      <span className="text-sm font-bold text-brand">
                        {selectedRequest.effectiveDays || (selectedRequest.requestedDays + selectedRequest.sandwichDays)} Days
                      </span>
                    </div>
                  </>
                )}
                <div className="flex items-center space-x-2">
                  {getStatusIcon(selectedRequest.status)}
                  <span className="text-sm text-gray-600">Status:</span>
                  <span
                    className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(
                      selectedRequest.status
                    )}`}
                  >
                    {selectedRequest.status.charAt(0).toUpperCase() +
                      selectedRequest.status.slice(1)}
                  </span>
                </div>
                {selectedRequest.leaveBalance && (
                  <div className="bg-white rounded-lg p-3 border border-blue-200 mt-3">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Leave Balance</h4>
                    <div className="grid grid-cols-3 gap-4 text-xs">
                      <div className="text-center">
                        <div className="text-gray-500">Total</div>
                        <div className="font-medium text-brand">{selectedRequest.leaveBalance.total}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500">Used</div>
                        <div className="font-medium text-red-600">{selectedRequest.leaveBalance.used}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500">Remaining</div>
                        <div className="font-medium text-green-600">{selectedRequest.leaveBalance.remaining}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Raised By Information */}
          {selectedRequest.raisedBy && (
            <div className="mt-6 bg-amber-50 rounded-lg p-4 border border-amber-100">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-amber-500" />
                Raised By
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-amber-400 flex-shrink-0" />
                  <span className="text-sm text-gray-600">Name:</span>
                  <span className="text-sm font-medium capitalize">
                    {selectedRequest.raisedBy.name || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-amber-400 flex-shrink-0" />
                  <span className="text-sm text-gray-600">Employee ID:</span>
                  <span className="text-sm font-medium">
                    {selectedRequest.raisedBy.employeeId || 'N/A'}
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <Mail className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-600">Email:</span>
                  <span className="text-sm font-medium break-words" style={{ wordBreak: 'break-word' }}>
                    {selectedRequest.raisedBy.email || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-4 w-4 text-amber-400 flex-shrink-0" />
                  <span className="text-sm text-gray-600">Department:</span>
                  <span className="text-sm font-medium">
                    {selectedRequest.raisedBy.department || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Reason */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Reason</h3>
            <p className="text-sm text-gray-600 break-words whitespace-pre-wrap">
              {selectedRequest.reason || 'No reason provided'}
            </p>
          </div>

          {/* Document Proof */}
          {selectedRequest.documentUrl && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-center gap-2">
                <FileText className="h-5 w-5 text-gray-500" />
                Attached Document
              </h3>
              <a
                href={selectedRequest.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-blue-600 bg-white hover:bg-gray-50 hover:text-blue-700 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                <span>View Document Proof</span>
              </a>
            </div>
          )}

          {/* ── PENDING: Inline Approve / Reject with Remark ── */}
          {selectedRequest.status === 'pending' && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Actions</h3>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remark / Comment
                    <span className="ml-1 text-xs text-gray-400 font-normal">(Required for rejection)</span>
                  </label>
                  <textarea
                    value={approvalComment}
                    onChange={(e) => setApprovalComment(e.target.value)}
                    rows={3}
                    maxLength={200}
                    placeholder="Add a remark or comment..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand resize-none"
                  />
                  <div className="text-xs text-gray-400 text-right mt-1">{approvalComment.length}/200</div>
                </div>
                <div className="flex gap-3 justify-end">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (!approvalComment.trim()) {
                        toast.error('Please provide a reason for rejection');
                        return;
                      }
                      handleRejectRequest(selectedRequest.id, approvalComment);
                    }}
                    className="flex items-center gap-1.5 border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                  >
                    <XCircle className="h-4 w-4" />
                    Reject
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleApproveRequest(selectedRequest.id, approvalComment)}
                    className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Approve
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ── APPROVED / REJECTED: Show stored HR comment ── */}
          {(selectedRequest.status === 'approved' || selectedRequest.status === 'rejected') && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {selectedRequest.status === 'approved' ? 'Approval Comment' : 'Rejection Reason'}
              </h3>
              {selectedRequest.comments ? (
                <div
                  className={`flex items-start gap-3 rounded-lg p-4 border ${selectedRequest.status === 'approved'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                    }`}
                >
                  {selectedRequest.status === 'approved' ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  )}
                  <p
                    className={`text-sm break-words whitespace-pre-wrap ${selectedRequest.status === 'approved' ? 'text-green-800' : 'text-red-800'
                      }`}
                  >
                    {selectedRequest.comments.charAt(0).toUpperCase() + selectedRequest.comments.slice(1)}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No remark provided.</p>
              )}
            </div>
          )}

        </div>
        </>
      ) : showRequestForm ? (
        <>
          <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowRequestForm(false);
                if (onClose) onClose();
              }}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900">
              {editingRequest ? "Edit Leave Request" : "New Leave Request"}
            </h2>
          </div>
        </div>

        <LeaveRequestForm
          formData={formData}
          setFormData={setFormData}
          formErrors={formErrors}
          handleInputChange={handleInputChange}
          handleBlur={handleBlur}
          handleSubmit={handleSubmitRequest}
          onCancel={() => {
            setShowRequestForm(false);
            setFormErrors({});
            setSelectedEmployeeData(null);
            setEmployeeSearchOpen(false);
            setEmployeeSearchTerm("");
            setFormData({
              leavePolicyId: "",
              startDate: "",
              endDate: "",
              reason: "",
              status: "pending",
              isActive: true,
              employeeId: "",
              employeeName: "",
              employeeEmail: "",
              requestedDays: 0,
              isHalfDay: false,
              halfDayType: "first_half",
              documentUrl: "",
              documentPublicId: "",
              documentResourceType: "raw"
            });
            setEditingRequest(null);
            if (onClose) onClose();
          }}
          leavePolicies={leavePolicies}
          loadingPolicies={loadingPolicies}
          selectedEmployeeData={selectedEmployeeData}
          setSelectedEmployeeData={setSelectedEmployeeData}
          employeeSearchOpen={employeeSearchOpen}
          setEmployeeSearchOpen={setEmployeeSearchOpen}
          employeeSearchTerm={employeeSearchTerm}
          setEmployeeSearchTerm={setEmployeeSearchTerm}
          dropdownEmployees={dropdownEmployees}
          isLoadingEmployeeSearch={isLoadingEmployeeSearch}
          debouncedEmployeeSearch={debouncedEmployeeSearch}
          validateField={validateField}
          isUploading={isUploading}
          handleFileUpload={handleFileUpload}
          handleRemoveFile={handleRemoveFile}
          isValidating={isValidating}
          editingRequest={editingRequest}
          handleTextKeyPress={handleTextKeyPress}
          setSelectedEmployee={setSelectedEmployee}
        />
        </>
      ) : (
        <>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Leave Requests
            </h2>
          </div>
          <div className="flex items-center space-x-4">
            {selectedRows.size > 0 && (
              <PermissionGate permission="attendance" action="delete">
                <Button
                  variant="primary"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={handleBulkDelete}
                  className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
                >
                  Delete ({selectedRows.size})
                </Button>
              </PermissionGate>
            )}
            <PermissionGate permission="attendance" action="read">
              <div className="relative shrink-0 mr-2" ref={selectedExportWrapRef}>
                <Button
                  variant="outline"
                  icon={exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent" /> : <Download className="h-4 w-4" />}
                  onClick={handleExportClick}
                  disabled={exportLoading}
                  className={selectedRows.size > 0 ? "bg-brand/5 border-brand/20 text-brand" : ""}
                >
                  {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : "Export"}
                </Button>
                <SelectedExportFormatBar
                  show={selectedRows.size > 0 && showInlineSelectedFormats}
                  panelEntered={inlineFormatPanelEntered}
                  disabled={exportLoading}
                  onSelect={handleLeaveSelectedFormatExport}
                />
              </div>
            </PermissionGate>
            <PermissionGate permission="attendance" action="create">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowRequestForm(true)}
                className="flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>New Request</span>
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by Employee Name or Email ID ..."
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
                setCurrentPage(1);
              }}
              maxLength={100}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  // Maintain focus on search input
                  const searchInput = document.querySelector('input[placeholder="Search requests..."]');
                  if (searchInput) {
                    setTimeout(() => searchInput.focus(), 0);
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Type Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            >
              <option value="all">All Types</option>
              {[
                "Annual Leave",
                "Sick Leave",
                "Personal Leave",
                "Maternity Leave",
                "Paternity Leave",
                "Emergency Leave",
                "Compensatory Off"
              ].map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Date Filter Range */}
          <div className="flex items-center gap-2">
            <DateRangeFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              allowFutureDates={true}
              onChange={(range) => {
                setFilterStartDate(range.startDate);
                setFilterEndDate(range.endDate);
                setCurrentPage(1);
              }}
            />

            {/* Clear Filters */}
            {(searchTerm || filterStatus !== "all" || filterType !== "all" || filterStartDate || filterEndDate) && (
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  setSearchTerm("");
                  setFilterStatus("all");
                  setFilterType("all");
                  setFilterStartDate("");
                  setFilterEndDate("");
                }}
                className="h-[44px] border-gray-200 text-gray-600 hover:bg-blue-50/30 transition-all rounded-lg shadow-sm px-4 py-0 text-sm font-semibold"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
            <tr>
              <th className="px-4 py-3 text-left w-12 bg-gray-50">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-brand focus:ring-brand h-3 w-3"
                  checked={leaveRequests.length > 0 && selectedRows.size === leaveRequests.length}
                  onChange={handleSelectAll}
                />
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-blue-100/60 select-none bg-gray-50"
                onClick={() => handleSort('employee')}
              >
                <div className="flex items-center space-x-1">
                  <span>Employee</span>
                  {sortField === 'employee' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-brand" /> :
                      <ArrowDown className="h-3 w-3 text-brand" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-blue-100/60 select-none bg-gray-50"
                onClick={() => handleSort('leaveType')}
              >
                <div className="flex items-center space-x-1">
                  <span>Leave Type</span>
                  {sortField === 'leaveType' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-brand" /> :
                      <ArrowDown className="h-3 w-3 text-brand" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-blue-100/60 select-none bg-gray-50"
                onClick={() => handleSort('startDate')}
              >
                <div className="flex items-center space-x-1">
                  <span>Duration</span>
                  {sortField === 'startDate' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-brand" /> :
                      <ArrowDown className="h-3 w-3 text-brand" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-blue-100/60 select-none bg-gray-50"
                onClick={() => handleSort('balance')}
              >
                <div className="flex items-center space-x-1">
                  <span>Balance</span>
                  {sortField === 'balance' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-brand" /> :
                      <ArrowDown className="h-3 w-3 text-brand" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[180px] cursor-pointer hover:bg-blue-100/60 select-none bg-gray-50"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center space-x-1">
                  <span>Applied Date</span>
                  {sortField === 'createdAt' || sortField === 'appliedDate' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-brand" /> :
                      <ArrowDown className="h-3 w-3 text-brand" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-blue-100/60 select-none bg-gray-50"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center space-x-1">
                  <span>Status</span>
                  {sortField === 'status' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-brand" /> :
                      <ArrowDown className="h-3 w-3 text-brand" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[130px] bg-gray-50">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading || isSearching ? (
              <tr>
                <td colSpan="9" className="px-6 py-8 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand"></div>
                    <span className="text-gray-500">
                      {isSearching ? "Searching..." : "Loading leave requests..."}
                    </span>
                  </div>
                </td>
              </tr>
            ) : leaveRequests.length > 0 ? (
              getSortedRequests(leaveRequests).map((request) => (
                <tr key={request.id} className={`hover:bg-blue-50/30 ${selectedRows.has(request.id) ? 'bg-brand/5' : ''}`}>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-brand focus:ring-brand h-3 w-3"
                      checked={selectedRows.has(request.id)}
                      onChange={() => handleSelectRow(request.id)}
                    />
                  </td>
                  <td className="px-4 py-4 overflow-hidden">
                    <div className="min-w-0 max-w-[220px]">
                      <div className="text-sm font-medium text-gray-900 truncate capitalize max-w-[220px]">
                        {request.employeeName}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-[220px]">
                        {request.employeeEmail}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 truncate capitalize overflow-hidden">
                    {request.leaveType}
                    {request.isHalfDay && (
                      <span className="block text-[10px] leading-tight font-medium text-brand mt-0.5">
                        Half Day ({request.halfDayType === 'first_half' ? '1st Half' : '2nd Half'})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="min-w-0">
                      <span>{request.startDate ? formatDate(request.startDate, { includeTime: false }) : 'N/A'}</span>
                      {!request.isHalfDay && request.endDate && request.endDate !== request.startDate && (
                        <>
                          <span className="text-gray-400 mx-1">-</span>
                          <span>{formatDate(request.endDate, { includeTime: false })}</span>
                        </>
                      )}
                      <div className="flex flex-col">
                        <span className="text-gray-900">
                          {request.requestedDays || request.totalDays} {(request.requestedDays || request.totalDays) === 1 ? 'day' : 'days'}
                        </span>
                        {request.sandwichDays > 0 && (
                          <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1 rounded border border-amber-100 w-fit">
                            +{request.sandwichDays} Sandwich
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(() => {
                      // If balance total is 0, try to get from policy
                      let balance = request.leaveBalance || { total: 0, used: 0, remaining: 0 };

                      if (balance.total === 0 && request.leavePolicyId && leavePolicies.length > 0) {
                        const policy = leavePolicies.find(p =>
                          p._id === request.leavePolicyId || p.id === request.leavePolicyId
                        );
                        if (policy) {
                          balance = {
                            total: policy.entitlement || 0,
                            used: balance.used || 0,
                            remaining: (policy.entitlement || 0) - (balance.used || 0)
                          };
                        }
                      }

                      return (
                        <div className="text-xs">
                          <div className="text-gray-600">Remaining: <span className="font-medium text-green-600">{balance.remaining}</span></div>
                          <div className="text-gray-500">Used: {balance.used} / {balance.total}</div>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(request.appliedDateTime || request.appliedDate, { includeTime: true })}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(request.status)}
                      <span
                        className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(
                          request.status
                        )}`}
                      >
                        {request.status.charAt(0).toUpperCase() +
                          request.status.slice(1)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(request)}
                        className="text-brand hover:text-brand-dark"
                        title="View Details"
                        aria-label="View leave request details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>

                      <PermissionGate permission="attendance" action="delete">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRequest(request)}
                          className="text-red-500 hover:text-red-800"
                          title="Delete Request"
                          aria-label="Delete leave request"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </PermissionGate>


                      {/* Approve/Reject buttons - only for HR/Admin, and not for own requests */}
                      {(() => {
                        // Get request employee ID (handle both string and object formats)
                        const requestEmployeeId = request.employeeId?._id || request.employeeId || null;

                        // Check if current user is the request creator
                        // Compare employee IDs (request.employeeId is the Employee document _id)
                        const isOwnRequest = currentUserEmployeeId && requestEmployeeId &&
                          (currentUserEmployeeId.toString() === requestEmployeeId.toString());

                        // Also check by email as fallback
                        const userEmail = user?.email?.toLowerCase().trim();
                        const requestEmail = request.employeeEmail?.toLowerCase().trim();
                        const isOwnRequestByEmail = userEmail && requestEmail && userEmail === requestEmail;

                        const isOwnRequestFinal = isOwnRequest || isOwnRequestByEmail;

                        // Show buttons only if:
                        // 1. User is HR manager or super admin
                        // 2. Request status is pending
                        // 3. It's NOT the user's own request
                        const shouldShowButtons = (userRole === "hr_manager" || userRole === "super_admin") &&
                          request.status === "pending" &&
                          !isOwnRequestFinal;

                        return shouldShowButtons ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => showApprovalConfirmation(request)}
                              className="text-green-600 hover:text-green-900"
                              title="Approve Request"
                              aria-label="Approve leave request"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => showRejectionConfirmation(request)}
                              className="text-red-600 hover:text-red-900"
                              title="Reject Request"
                              aria-label="Reject leave request"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        ) : null;
                      })()}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                  No leave requests found for the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(() => {
        const leaveTotalItems = Number(pagination?.totalItems || leaveRequests.length || 0);
        const leaveTotalPages = Number(pagination?.totalPages || 1);
        const effectivePaginationLimit = pagination?.limit || pagination?.itemsPerPage || itemsPerPage || 10;
        const currentPageNum = pagination?.currentPage || currentPage || 1;
        const shouldShowPagination = !loading && !isSearching && leaveTotalItems > 0;

        // Calculate hasPrevPage and hasNextPage if not provided
        const hasPrevPage = pagination?.hasPrevPage !== undefined
          ? pagination.hasPrevPage
          : currentPageNum > 1;
        const hasNextPage = pagination?.hasNextPage !== undefined
          ? pagination.hasNextPage
          : currentPageNum < leaveTotalPages;

        const handleLeavePageChange = (page) => {
          setCurrentPage(page);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        return shouldShowPagination ? (
          <PaginationControls
            currentPage={currentPageNum}
            totalPages={leaveTotalPages}
            totalItems={leaveTotalItems}
            limit={effectivePaginationLimit}
            hasNextPage={hasNextPage}
            hasPrevPage={hasPrevPage}
            onPageChange={handleLeavePageChange}
            onLimitChange={handleItemsPerPageChange}
          />
        ) : null;
      })()}

      {!loading && !isSearching && leaveRequests.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {searchMetadata && searchMetadata.appliedFilters > 0
              ? "No leave requests found matching your search criteria"
              : "No leave requests found"
            }
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchMetadata && searchMetadata.appliedFilters > 0
              ? "Try adjusting your search criteria or clear filters to see all requests."
              : "Create a new leave request to get started."
            }
          </p>
        </div>
      )}
    </>
    )}

    {/* Approval Confirmation Modal */}
      {showApproveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Approve Leave Request</h3>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to approve this leave request for{" "}
              <span className="font-semibold">{pendingActionRequest?.employeeName}</span>?
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments (Optional)
              </label>
              <textarea
                value={approvalComment}
                onChange={(e) => setApprovalComment(e.target.value)}
                rows={3}
                placeholder="Add any comments..."
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand resize-none"
              />
              <div className="text-xs text-gray-500 text-right mt-1">
                {approvalComment.length}/200
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowApproveConfirm(false);
                  setPendingActionRequest(null);
                  setApprovalComment("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmApproval}
                className="bg-green-600 hover:bg-green-700"
              >
                Approve
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Confirmation Modal */}
      {showRejectConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Reject Leave Request</h3>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to reject this leave request for{" "}
              <span className="font-semibold">{pendingActionRequest?.employeeName}</span>?
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionComment}
                onChange={(e) => setRejectionComment(e.target.value)}
                rows={3}
                placeholder="Please provide a reason for rejection..."
                maxLength={200}
                required
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand resize-none ${!rejectionComment.trim() ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
              />
              <div className="text-xs text-gray-500 text-right mt-1">
                {rejectionComment.length}/200
              </div>
              {!rejectionComment.trim() && (
                <p className="text-xs text-red-600 mt-1">Rejection reason is required</p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRejectConfirm(false);
                  setPendingActionRequest(null);
                  setRejectionComment("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmRejection}
                className="bg-red-600 hover:bg-red-700"
                disabled={!rejectionComment.trim()}
              >
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sandwich Rule Confirmation Modal */}
      {showSandwichConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in duration-200">
            <div className="bg-amber-50 p-6 border-b border-amber-100">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="h-7 w-7 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Sandwich Rule Impact</h3>
                  <p className="text-xs text-amber-700 font-medium uppercase tracking-wider">Policy Alert</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Based on the <span className="font-bold text-gray-900">Sandwich Rule</span>, the holidays/weekends falling between your leave dates will also be deducted.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-white border border-gray-100 rounded-xl shadow-sm text-center">
                    <div className="text-[10px] text-gray-400 uppercase font-bold mb-1">Requested</div>
                    <div className="text-xl font-black text-gray-700">{sandwichValidationResult?.requestedDays} Days</div>
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl shadow-sm text-center">
                    <div className="text-[10px] text-amber-500 uppercase font-bold mb-1">Sandwich Impact</div>
                    <div className="text-xl font-black text-amber-600">+{sandwichValidationResult?.sandwichDays} Days</div>
                  </div>
                </div>

                <div className="p-4 bg-brand/5 rounded-xl border border-brand/20 flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-700">Total Deduction:</span>
                  <span className="text-xl font-black text-brand">{sandwichValidationResult?.effectiveDays} Days</span>
                </div>

                {sandwichValidationResult?.affectedDates?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase mb-2">Affected Holidays/Weekends:</p>
                    <div className="flex flex-wrap gap-2">
                      {sandwichValidationResult.affectedDates.map((date, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 text-[10px] font-bold rounded-md border border-gray-200">
                          {new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowSandwichConfirm(false);
                  setSandwichValidationResult(null);
                }}
              >
                Change Dates
              </Button>
              <Button
                variant="primary"
                onClick={async () => {
                  setShowSandwichConfirm(false);
                  await processSubmit();
                }}
              >
                Confirm & Submit
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Leave Requests"
        showDateRange={true}
        scopedFormatExport={{
          onExport: handleLeaveScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Delete Confirmation Modal */}
      <WarningPopup
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setRequestToDelete(null);
          setIsBulkDelete(false);
        }}
        onConfirm={confirmDeleteRequest}
        title={isBulkDelete ? "Delete Leave Requests" : "Delete Leave Request"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedRows.size} selected leave requests? This action cannot be undone.`
            : "Are you sure you want to delete this leave request? This action cannot be undone."
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
};

export default LeaveRequests;

